<main class="intake-portal-page">
  <header class="intake-portal-page__header">
    <p class="intake-portal-page__eyebrow">Onboarding</p>
    <h1 class="intake-portal-page__title">{{ page_title }}</h1>
    <p class="intake-portal-page__subtitle">
      {{ page_subtitle }}
    </p>
  </header>

  <section v-if="is_loading_portal" class="intake-portal-card">
    <header class="intake-portal-card__header">
      <h2 class="intake-portal-card__title">Loading intake</h2>
      <p class="intake-portal-card__subtitle">Fetching the active onboarding form.</p>
    </header>
  </section>

  <section v-else-if="has_entity_type_options" class="intake-portal-preview-switcher">
    <div class="intake-portal-preview-switcher__inner">
      <div class="intake-portal-preview-switcher__group intake-portal-preview-switcher__group--form">
        <label class="intake-portal-preview-switcher__label">Choose onboarding type</label>
        <div class="intake-portal-journey-picker">
          <button
            v-for="entity_type in entity_type_options"
            :key="get_entity_type_option_id(entity_type)"
            type="button"
            :class="entity_type_button_class(entity_type)"
            :disabled="is_starting_onboarding"
            @click="handleEntityTypeChange(entity_type)"
          >
            {{ entity_type_label(entity_type) }}
          </button>
          <button
            type="button"
            class="intake-portal-card__continue intake-portal-start-button"
            :disabled="!can_start_selected_intake"
            @click="handleStartSelectedIntake"
          >
            <span>{{ intake_action_label }}</span>
            <i class="bi bi-arrow-right" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div v-if="application_forms.length > 1" class="intake-portal-preview-switcher__group intake-portal-preview-switcher__group--form">
        <label for="intake_form_select" class="intake-portal-preview-switcher__label">Form</label>
        <select
          id="intake_form_select"
          v-model="selected_application_form_id"
          class="intake-portal-preview-switcher__select"
          @change="handleFormChange"
        >
          <option v-for="form in application_forms" :key="get_application_form_id(form)" :value="String(get_application_form_id(form))">
            {{ get_application_form_name(form) }}
          </option>
        </select>
      </div>
    </div>
  </section>

  <section v-else-if="!is_loading_portal" class="intake-portal-card">
    <header class="intake-portal-card__header">
      <h2 class="intake-portal-card__title">No onboarding types available</h2>
      <p class="intake-portal-card__subtitle">
        There are no configured entity types available for your login yet.
      </p>
    </header>
  </section>

  <section v-if="selected_application_id" class="intake-portal-preview-switcher">
    <div class="intake-portal-preview-switcher__inner">
      <div class="intake-portal-preview-switcher__group">
        <label class="intake-portal-preview-switcher__label">Progress</label>
        <div class="intake-portal-journey-picker">
          <span class="intake-portal-journey-pill intake-portal-journey-pill--default is-active">
            {{ completion_label }}
          </span>
        </div>
      </div>
    </div>
  </section>

  <div v-if="portal_error" class="alert alert-warning intake-portal-alert" role="alert">
    {{ portal_error }}
  </div>

  <div v-if="submit_success_message" class="alert alert-success intake-portal-alert" role="alert">
    {{ submit_success_message }}
  </div>

  <section v-if="is_starting_onboarding" class="intake-portal-card">
    <header class="intake-portal-card__header">
      <h2 class="intake-portal-card__title">Preparing {{ selected_journey_label }}</h2>
      <p class="intake-portal-card__subtitle">Setting up the forms and workflow for this onboarding type.</p>
    </header>
  </section>

  <nav v-if="intake_steps.length" class="intake-portal-steps" aria-label="Applicant intake steps">
    <ol class="intake-portal-steps__list">
      <li
        v-for="step in intake_steps"
        :key="step.id"
        :class="['intake-portal-steps__item', { 'is-active': is_active_step(step) }]"
      >
        <span class="intake-portal-steps__number">{{ step.number }}</span>
        <span class="intake-portal-steps__label">{{ step.label }}</span>
      </li>
    </ol>
  </nav>

  <section v-if="current_main_section" class="intake-portal-card" aria-labelledby="intake_portal_step_title">
    <header class="intake-portal-card__header">
      <h2 id="intake_portal_step_title" class="intake-portal-card__title">{{ card_title }}</h2>
      <p class="intake-portal-card__subtitle">{{ card_subtitle }}</p>
    </header>

    <form class="intake-portal-form" @submit.prevent="submit_or_continue">
      <div
        v-if="Array.isArray(current_form_instructions) && current_form_instructions.length"
        class="border rounded-3 bg-light-subtle px-3 py-3 mb-4"
      >
        <p
          v-for="(instruction, index) in current_form_instructions"
          :key="`form-instruction-${index}`"
          :class="['small text-muted mb-0 queue-form-instruction-tex', { 'mt-2': index > 0 }]"
        >
          {{ instruction }}
        </p>
      </div>

        <div
          v-for="sub_section in current_main_section.sub_sections"
          :key="sub_section.id"
          class="intake-portal-section-block"
        >
        <div v-if="should_show_sub_section_title(sub_section)" class="intake-portal-section-block__header">
          <h3 class="intake-portal-section-block__title">{{ sub_section.label }}</h3>
        </div>

        <div
          v-if="Array.isArray(sub_section.instructions) && sub_section.instructions.length"
          class="border rounded-3 bg-light-subtle px-3 py-3 mb-3"
        >
          <p
            v-for="(instruction, index) in sub_section.instructions"
            :key="`${sub_section.id}-instruction-${index}`"
            :class="['small text-muted mb-0 queue-form-instruction-tex', { 'mt-2': index > 0 }]"
          >
            {{ instruction }}
          </p>
        </div>

        <div class="intake-portal-form__grid">
          <div
            v-for="question in visible_questions_for_sub_section(sub_section)"
            :key="question.id"
            :class="[
              'intake-portal-field',
              {
                'intake-portal-field--full': uses_textarea(question) || uses_checkbox_group(question) || uses_file(question) || is_dependent_question(question),
                'intake-portal-field--dependent': is_dependent_question(question)
              }
            ]"
          >
            <div :class="['intake-portal-field__shell', { 'intake-portal-field__shell--dependent': is_dependent_question(question) }]">
              <div v-if="is_dependent_question(question)" class="intake-portal-dependent-callout">
                <span class="intake-portal-dependent-callout__line" aria-hidden="true"></span>
                <div class="intake-portal-dependent-card">
                  <label
                    :for="`question_${question.id}`"
                    :class="['intake-portal-field__label', { 'intake-portal-field__label--file': uses_file(question) }]"
                  >
                    <span>{{ question.label }}<span v-if="question.is_mandatory"> *</span></span>
                    <span v-if="uses_file(question)" class="intake-portal-field__file-cue">
                      <i class="fa-solid fa-plus" aria-hidden="true"></i>
                    </span>
                  </label>

                  <p v-if="question.description" class="intake-portal-field__hint">{{ question.description }}</p>

                  <textarea
                    v-if="uses_textarea(question)"
                    :id="`question_${question.id}`"
                    v-model="form_values[question.id]"
                    rows="4"
                    class="intake-portal-field__control intake-portal-field__control--textarea"
                  ></textarea>

                  <select
                    v-else-if="uses_select(question)"
                    :id="`question_${question.id}`"
                    v-model="form_values[question.id]"
                    class="intake-portal-field__control"
                  >
                    <option value="">Select an option</option>
                    <option v-for="option in question.options" :key="option.id" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>

                  <div v-else-if="uses_radio(question)" class="intake-portal-choice-list">
                    <label
                      v-for="option in question.options"
                      :key="option.id"
                      class="intake-portal-choice"
                    >
                      <input
                        v-model="form_values[question.id]"
                        type="radio"
                        :name="`question_${question.id}`"
                        :value="option.value"
                      >
                      <span>{{ option.label }}</span>
                    </label>
                  </div>

                  <div v-else-if="uses_checkbox_group(question)" class="intake-portal-choice-list">
                    <label
                      v-for="option in question.options"
                      :key="option.id"
                      class="intake-portal-choice"
                    >
                      <input
                        :checked="get_question_value(question).includes(option.value)"
                        type="checkbox"
                        @change="update_multi_select_value(question.id, option.value, $event.target.checked)"
                      >
                      <span>{{ option.label }}</span>
                    </label>
                  </div>

                  <FacialVerificationField
                    v-else-if="uses_facial_verification(question)"
                    :application-form-id="Number(selected_application_form_id || 0)"
                    :question="question"
                    :model-value="get_facial_verification_value(question.id)"
                    @update:modelValue="update_facial_verification_value(question.id, $event)"
                  />

                  <label
                    v-else-if="uses_file(question)"
                    :for="`question_${question.id}`"
                    class="intake-portal-file"
                  >
                    <input
                      :id="`question_${question.id}`"
                      type="file"
                      class="d-none"
                      multiple
                      @change="handle_file_change(question.id, $event)"
                    >
                    <span class="intake-portal-file__button">Choose files</span>
                    <span class="intake-portal-file__name">{{ format_file_name(file_values[question.id], question.id) }}</span>
                    <span
                      v-for="(file, file_index) in visible_question_files(question.id)"
                      :key="`${question.id}_${file.id || file.name || file_index}`"
                      class="intake-portal-file__name"
                    >
                      {{ file.name || 'Uploaded file' }}
                      <button
                        type="button"
                        class="btn btn-link btn-sm p-0 ms-2"
                        @click.prevent="remove_question_file(question.id, file_index)"
                      >
                        Remove
                      </button>
                    </span>
                    <button
                      v-if="file_values[question.id]"
                      type="button"
                      class="btn btn-link btn-sm text-danger p-0 ms-2"
                      aria-label="Remove selected file"
                      @click.stop.prevent="clear_selected_file(question.id)"
                    >
                      <i class="bi bi-x-lg" aria-hidden="true"></i>
                    </button>
                  </label>

                  <label v-else-if="uses_checkbox(question)" class="intake-portal-choice intake-portal-choice--single">
                    <input
                      v-model="form_values[question.id]"
                      type="checkbox"
                    >
                    <span>Yes</span>
                  </label>

                  <input
                    v-else
                    :id="`question_${question.id}`"
                    v-model="form_values[question.id]"
                    :type="question_input_type(question)"
                    class="intake-portal-field__control"
                  />
                </div>
              </div>

              <template v-else>
                <label
                  :for="`question_${question.id}`"
                  :class="['intake-portal-field__label', { 'intake-portal-field__label--file': uses_file(question) }]"
                >
                  <span>{{ question.label }}<span v-if="question.is_mandatory"> *</span></span>
                  <span v-if="uses_file(question)" class="intake-portal-field__file-cue">
                    <i class="fa-solid fa-plus" aria-hidden="true"></i>
                  </span>
                </label>

                <p v-if="question.description" class="intake-portal-field__hint">{{ question.description }}</p>

                <textarea
                  v-if="uses_textarea(question)"
                  :id="`question_${question.id}`"
                  v-model="form_values[question.id]"
                  rows="4"
                  class="intake-portal-field__control intake-portal-field__control--textarea"
                ></textarea>

                <select
                  v-else-if="uses_select(question)"
                  :id="`question_${question.id}`"
                  v-model="form_values[question.id]"
                  class="intake-portal-field__control"
                >
                  <option value="">Select an option</option>
                  <option v-for="option in question.options" :key="option.id" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>

                <div v-else-if="uses_radio(question)" class="intake-portal-choice-list">
                  <label
                    v-for="option in question.options"
                    :key="option.id"
                    class="intake-portal-choice"
                  >
                    <input
                      v-model="form_values[question.id]"
                      type="radio"
                      :name="`question_${question.id}`"
                      :value="option.value"
                    >
                    <span>{{ option.label }}</span>
                  </label>
                </div>

                <div v-else-if="uses_checkbox_group(question)" class="intake-portal-choice-list">
                  <label
                    v-for="option in question.options"
                    :key="option.id"
                    class="intake-portal-choice"
                  >
                    <input
                      :checked="get_question_value(question).includes(option.value)"
                      type="checkbox"
                      @change="update_multi_select_value(question.id, option.value, $event.target.checked)"
                    >
                    <span>{{ option.label }}</span>
                  </label>
                </div>

                <FacialVerificationField
                  v-else-if="uses_facial_verification(question)"
                  :application-form-id="Number(selected_application_form_id || 0)"
                  :question="question"
                  :model-value="get_facial_verification_value(question.id)"
                  @update:modelValue="update_facial_verification_value(question.id, $event)"
                />

                <label
                  v-else-if="uses_file(question)"
                  :for="`question_${question.id}`"
                  class="intake-portal-file"
                >
                  <input
                    :id="`question_${question.id}`"
                    type="file"
                    class="d-none"
                    multiple
                    @change="handle_file_change(question.id, $event)"
                  >
                  <span class="intake-portal-file__button">Choose files</span>
                  <span class="intake-portal-file__name">{{ format_file_name(file_values[question.id], question.id) }}</span>
                  <span
                    v-for="(file, file_index) in visible_question_files(question.id)"
                    :key="`${question.id}_${file.id || file.name || file_index}`"
                    class="intake-portal-file__name"
                  >
                    {{ file.name || 'Uploaded file' }}
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0 ms-2"
                      @click.prevent="remove_question_file(question.id, file_index)"
                    >
                      Remove
                    </button>
                  </span>
                  <button
                    v-if="file_values[question.id]"
                    type="button"
                    class="btn btn-link btn-sm text-danger p-0 ms-2"
                    aria-label="Remove selected file"
                    @click.stop.prevent="clear_selected_file(question.id)"
                  >
                    <i class="bi bi-x-lg" aria-hidden="true"></i>
                  </button>
                </label>

                <label v-else-if="uses_checkbox(question)" class="intake-portal-choice intake-portal-choice--single">
                  <input
                    v-model="form_values[question.id]"
                    type="checkbox"
                  >
                  <span>Yes</span>
                </label>

                <input
                  v-else
                  :id="`question_${question.id}`"
                  v-model="form_values[question.id]"
                  :type="question_input_type(question)"
                  class="intake-portal-field__control"
                />
              </template>
            </div>
          </div>
        </div>
      </div>

      <footer class="intake-portal-card__footer">
        <button
          type="button"
          class="intake-portal-card__back"
          :disabled="current_step === 1 || is_submitting"
          @click="go_to_previous_step"
        >
          Back
        </button>

        <span class="intake-portal-card__step">{{ current_step_label }}</span>

        <button type="submit" class="intake-portal-card__continue" :disabled="is_submitting">
          <span>{{ is_submitting ? 'Submitting...' : submit_button_label }}</span>
          <i class="bi bi-arrow-right" aria-hidden="true"></i>
        </button>
      </footer>
    </form>
  </section>

  <section v-else-if="!is_loading_portal && has_entity_type_options && !is_starting_onboarding" class="intake-portal-card">
    <header class="intake-portal-card__header">
      <h2 class="intake-portal-card__title">{{ empty_form_title }}</h2>
      <p class="intake-portal-card__subtitle">
        {{ empty_form_subtitle }}
      </p>
    </header>
  </section>
</main>
